import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { EmeraldButtonModule } from '@emerald-angular/button';
import { EmeraldDatepickerModule } from '@emerald-angular/datepicker';
import { EmeraldDropdownModule } from '@emerald-angular/dropdown';
import { EmeraldModalModule } from '@emerald-angular/modal';
import { GlobalErrorService } from '@services/global-error.service';
import { HiringManagerOpenpositionsService } from '@pages/hiring-manager/hiring-manager-openpositions/hiring-manager-openpositions.service';
import { dropdownValues} from '@interfaces/common.model';
import { PositionData } from '@interfaces/open-positions.model';
import { PersonaType } from '@constants/global.enum';
import { filterData, formatSingleDate } from '@utils/utils';
import { location } from '@pages/vendor/add-candidate/add-candidate.config';
import { yearDateFormat } from '@constants/dateFormat';
import { jobDetails } from '@interfaces/job-details.model';

@Component({
  selector: 'app-job-details-filter',
  standalone: true,
  imports: [
    EmeraldDropdownModule,
    ReactiveFormsModule,
    EmeraldDatepickerModule,
    EmeraldButtonModule,
    EmeraldModalModule,
    CommonModule,
  ],
  templateUrl: './job-details-filter.component.html',
  styleUrl: './job-details-filter.component.scss',
})
export class JobDetailsFilterComponent {
  @Input() fieldsVisibility: string = '';
  @Input() jobDtlsData: jobDetails[] = [];
  @Input() openpositionData: PositionData[] = [];
  @Input() requestId: number = 0;

  @Output() filteredData: EventEmitter<any> = new EventEmitter();

  filterForm: FormGroup = new FormGroup({});
  opened: boolean = false;
  name: dropdownValues[] = [];
  experience: dropdownValues[] = [];
  location: dropdownValues[] = [];
  status: dropdownValues[] = [];
  positions: dropdownValues[] = [];
  nameOptions: dropdownValues[] = [];
  vendorNameOptions: dropdownValues[] = [];
  position: dropdownValues[] = [];
  displayData: any[] = [];
  openForm: boolean = true;
  hiringRequestId: number = 0;
  personaType = PersonaType;
  locationOptions = location;

  constructor(
    public fb: FormBuilder,
    public globalError: GlobalErrorService,
    private openPositionsService: HiringManagerOpenpositionsService,
  ) {}

  ngOnInit() {
    this.hiringRequestId =
      this.openPositionsService.jobDetails?.[0]?.hiringRequestId || 0;
    this.initialzeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['jobDtlsData'] && changes['jobDtlsData'].currentValue) {
      this.displayData = this.jobDtlsData;

      // Generate dropdown options for all fields
      this.nameOptions = this.generateDropdownOptions('candidateName');
      this.vendorNameOptions = this.generateDropdownOptions('vendorName');
      this.position = this.generateDropdownOptions('position');
      this.experience = this.generateDropdownOptions('yearsOfExperience');
      this.location = this.generateDropdownOptions('location');
      this.status = this.generateDropdownOptions('status');
    }
  }

  // Helper function to generate dropdown options
  private generateDropdownOptions(key: string): dropdownValues[] {
    if (!this.jobDtlsData || this.jobDtlsData.length === 0) {
      return [];
    }

    return [...new Set(this.jobDtlsData.map((item: any) => item[key]))]
      .filter((value) => value !== null && value !== '' && value !== undefined)
      .map((value) => ({
        label: value,
        value,
      }));
  }

  initialzeForm() {
    this.filterForm = this.fb.group({
      selectedName: [null, Validators.required],
      selectedVendorName: [null, Validators.required],
      selectedExperience: [null, Validators.required],
      selectedLocation: [null, Validators.required],
      selectedStatus: [null, Validators.required],
      selectedReceivedOn: [null, Validators.required],
      selectedAvailableBy: [null, Validators.required],
      selectedPosition: [null, Validators.required],
    });
  }

  apply() {
    const getFilterValue = (field: string) =>
      this.filterForm.get(field)?.value || null;

    if (this.fieldsVisibility === PersonaType.HireMgrJobDet) {
      const selectedName = getFilterValue('selectedName');
      const selectedVendorName = getFilterValue('selectedVendorName');
      const selectedPosition = getFilterValue('selectedPosition');
      const selectedExperience = getFilterValue('selectedExperience');

      const selectedStatus = getFilterValue('selectedStatus');
      const selectedLocation = getFilterValue('selectedLocation');
      const selectedAvailableBy =
        getFilterValue('selectedAvailableBy') != null
          ? formatSingleDate(
              getFilterValue('selectedAvailableBy'),
              yearDateFormat.date,
            )
          : null;
      const selectedReceivedOn =
        getFilterValue('selectedReceivedOn') != null
          ? formatSingleDate(
              getFilterValue('selectedReceivedOn'),
              yearDateFormat.date,
            )
          : null;
      const hiringMgrJobDetls = {
        candidateName: selectedName,
        vendorName: selectedVendorName,
        position: selectedPosition,
        yearsOfExperience: selectedExperience,
        location: selectedLocation,
        status: selectedStatus,
        earliestAvailability: selectedAvailableBy,
        createdDate: selectedReceivedOn,
      };
      this.displayData = filterData(this.jobDtlsData, hiringMgrJobDetls);
    }

    if (
      this.fieldsVisibility === PersonaType.VendorRepJobDet ||
      this.fieldsVisibility === PersonaType.OpsTeamJobDet
    ) {
      const selectedCandName = getFilterValue('selectedName');
      const selectedVendorName = getFilterValue('selectedVendorName');
      const selectedPos = getFilterValue('selectedPosition');
      const selectedExperience = getFilterValue('selectedExperience');
      const selectedLocation = getFilterValue('selectedLocation');
      const selectedStatus = getFilterValue('selectedStatus');

      const vendorRepJobDtlsFilters = {
        candidateName: selectedCandName,
        vendorName: selectedVendorName,
        position: selectedPos,
        yearsOfExperience: selectedExperience,
        location: selectedLocation,
        status: selectedStatus,
      };

      this.displayData = filterData(this.jobDtlsData, vendorRepJobDtlsFilters);
    }
    this.filteredData.emit(this.displayData);
  }

  reset() {
    this.initialzeForm();
    this.openForm = false;
    setTimeout(() => {
      this.openForm = true;
    });
    this.filteredData.emit(this.jobDtlsData);
  }

  formatCandidates(job: jobDetails[]): jobDetails[] {
    return (job || []).map((job: jobDetails) => {
      job.createdDate = formatSingleDate(job.createdDate, yearDateFormat.date);
      job.earliestAvailability = formatSingleDate(
        job.earliestAvailability,
        yearDateFormat.date,
      );
      return job;
    });
  }
}
